import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { TaskNavigationTabsComponent } from '@fi-app/task/task-navigation-tabs/task-navigation-tabs.component';
import { TaskToCompleteContainerComponent } from '@fi-app/task/task-to-complete-container/task-to-complete-container.component';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { AccessRightType, ApplicationSettingsType, ModuleType, ObjectTypes } from '@fi-shared/types';
import { TaskCompletedContainerComponent } from '@fi-app/task/task-completed-container/task-completed-container.component';
import { TaskStatusContainerComponent } from '@fi-app/task/task-status-container/task-status-container.component';
import { TaskEditContainerComponent } from '@fi-app/task/task-edit-container/task-edit-container.component';
import { FiTaskSharedService } from '@fi-shared/fi-task-shared/fi-task-shared.service';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { TaskDetailsContainerComponent } from '@fi-app/task/task-details-container/task-details-container.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiCommentsService } from '@fi-shared/fi-comments/fi-comments.service';
import { TasksArchivedContainerComponent } from '@fi-app/task/tasks-archived-container/tasks-archived-container.component';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { coerceNumberProperty } from '@angular/cdk/coercion';

const TaskState: Ng2StateDeclaration = {
    name: FiStateNames.Task._name,
    url: '/Task',
    abstract: true,
    component: TaskNavigationTabsComponent,
};

const TaskToCompleteState: Ng2StateDeclaration = {
    name: FiStateNames.Task.ToComplete,
    url: '/ToComplete?{:tabNumber}',
    component: TaskToCompleteContainerComponent,
    reloadOnSearch: false,
    resolve: {
        selectedTabNumber: [FiStateService, Transition, getSelectedTabNumber],
    },
};

const TaskCompletedState: Ng2StateDeclaration = {
    name: FiStateNames.Task.Completed,
    url: '/Completed?{:tabNumber}',
    component: TaskCompletedContainerComponent,
    reloadOnSearch: false,
    resolve: {
        selectedTabNumber: [FiStateService, Transition, getSelectedTabNumber],
    },
};

export function getSelectedTabNumber(stateService: FiStateService, transition: Transition) {
    return +stateService.getParams(transition).tabNumber;
}

const TaskStatusState: Ng2StateDeclaration = {
    name: FiStateNames.Task.Status,
    url: '/Status',
    component: TaskStatusContainerComponent,
    resolve: {
        tasksStatus: [FiTaskSharedService, taskStatus_tasksStatus],
    },
};

export function taskStatus_tasksStatus(taskSharedService: FiTaskSharedService) {
    return taskSharedService.getTasksStatus();
}

const TaskAddState: Ng2StateDeclaration = {
    name: FiStateNames.Task.Add,
    url: '/Add',
    views: {
        '@Module': {
            component: TaskEditContainerComponent,
        },
    },
    resolve: {
        task: [
            FiTaskV2HelperService,
            (fiTaskV2HelperService: FiTaskV2HelperService) => {
                const task = fiTaskV2HelperService.getNewTaskV2Instance();
                return task;
            },
        ],
        accessRightFilterCollection: [AccessRightService, taskAdd_accessRightFilterCollection],
    },
};
export function taskAdd_accessRightFilterCollection(accessRightService: AccessRightService) {
    return accessRightService.getAccessRightsForPublicationByObjectType(ObjectTypes.Task);
}

const TaskEditsState: Ng2StateDeclaration = {
    name: FiStateNames.Task.Edit,
    url: '/Edit/{id:[0-9]+}',
    views: {
        '@Module': {
            component: TaskEditContainerComponent,
        },
    },
    resolve: {
        task: [FiStateService, Transition, FiTaskSharedService, taskEdit_task],
        accessRightFilterCollection: [AccessRightService, taskAdd_accessRightFilterCollection],
    },
};
export function taskEdit_task(fiStateService: FiStateService, tr: Transition, taskSharedService: FiTaskSharedService) {
    return taskSharedService.getTask(fiStateService.getParams(tr).id);
}

const TaskDetailsState: Ng2StateDeclaration = {
    name: FiStateNames.Task.Details,
    url: '/Details/{id:[0-9]+}',
    views: {
        '@Module': {
            component: TaskDetailsContainerComponent,
        },
    },
    resolve: {
        task: [FiStateService, Transition, FiTaskSharedService, taskDetailsState_task],
        accessRightFilterCollection: [AccessRightService, taskAdd_accessRightFilterCollection],
        comments: [FiStateService, Transition, FiCommentsService, taskDetailsState_comments],
    },
};

const TaskArchivedState: Ng2StateDeclaration = {
    name: FiStateNames.Task.Archived,
    url: '/Archived',
    component: TasksArchivedContainerComponent,
    resolve: {
        tasks: [FiTaskSharedService, taskArchived_tasks],
    },
};

export function taskArchived_tasks(taskSharedService: FiTaskSharedService) {
    return taskSharedService.getArchivedTasks();
}

export function taskDetailsState_task(
    fiStateService: FiStateService,
    tr: Transition,
    taskSharedService: FiTaskSharedService,
) {
    const taskId = coerceNumberProperty(fiStateService.getParams(tr).id);
    return taskSharedService.getTask(taskId);
}
export function taskDetailsState_comments(
    fiStateService: FiStateService,
    tr: Transition,
    commentsService: FiCommentsService,
) {
    return commentsService.getComments(fiStateService.getParams(tr).id, ObjectTypes.Task);
}

export const TaskDetailsCommentIdResolver = [
    FiStateService,
    Transition,
    (fiStateService: FiStateService, tr: Transition) => {
        return fiStateService.getParams(tr).commentId;
    },
];

export const TaskStates: StatesModule = {
    states: [
        TaskState,
        TaskToCompleteState,
        TaskCompletedState,
        TaskStatusState,
        TaskAddState,
        TaskEditsState,
        TaskDetailsState,
        TaskArchivedState,
    ],
};

export function registerTaskStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules(
        [
            FiStateNames.Task._name,
            FiStateNames.Task.ToComplete,
            FiStateNames.Task.Completed,
            FiStateNames.Task.Status,
            FiStateNames.Task.Add,
            FiStateNames.Task.Details,
            FiStateNames.Task.Archived,
        ],
        {
            SettingType: [ApplicationSettingsType.Task],
            AccessType: AccessRightType.Read,
        },
    );
}

export function taskHandleStateChange(tr: Transition, pageTitleService: PageTitleService) {
    // https://chainformation.atlassian.net/browse/FI-3805
    const toState = tr.to();
    if (TaskStates.states.find((state) => state.name === toState.name)) {
        pageTitleService.setTitleByTextId('task.tasks');
    }
}
